import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { OneWebComponentsAngularModule } from '@one/angular';
import { QnSbnService } from '../../services/qn-sbn-service.service';

interface FeedbackQuestionAnswer {
  FeedbackType?: string;
  FeedbackQuestion?: string;
  FeedbackAnswer?: string;
  MandatoryOrOptional?: string;
}

interface FeedbackDetailsRow {
  AccountId: string;
  AccountName: string;
  Status: string;
  IsLsoAcknowledged: boolean | string;
  CustomerAttachment: string;
  Submitter: string;
  QuestionsAndAnswers: FeedbackQuestionAnswer[];
}

@Component({
  selector: 'dl-feedback-details-audit-trail',
  standalone: true,
  imports: [CommonModule, OneWebComponentsAngularModule],
  templateUrl: './feedback-details-audit-trail.component.html',
  styleUrl: './feedback-details-audit-trail.component.scss'
})
export class FeedbackDetailsAuditTrailComponent implements OnChanges {
  @Input() inputData: any;
  @Input() notificationId = '';
  @Output() closed = new EventEmitter<void>();

  public displayedColumns = [
    'AccountId',
    'AccountName',
    'Status',
    'IsLsoAcknowledged',
    'CustomerAttachment',
    'Submitter',
    'QuestionsAndAnswers'
  ];
  public dataSource: FeedbackDetailsRow[] = [];
  public expandedElement: FeedbackDetailsRow | null = null;
  public isLoading = false;
  public errorMessage = '';

  public constructor(private readonly qnSbnService: QnSbnService) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['notificationId'] && this.notificationId) {
      this.loadFeedbackDetails(this.notificationId);
    } else if (changes['inputData']) {
      this.dataSource = this.toRows(changes['inputData'].currentValue);
    }
  }

  public expandToggle(element: FeedbackDetailsRow): void {
    this.expandedElement = this.expandedElement === element ? null : element;
  }

  public hasFeedbackDetails(element: FeedbackDetailsRow): boolean {
    return element.QuestionsAndAnswers.length > 0;
  }

  public close(): void {
    this.closed.emit();
  }

  private loadFeedbackDetails(notificationId: string): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.qnSbnService.getAccountsFeedbackResponses(notificationId).subscribe({
      next: response => {
        this.dataSource = this.toRows(response);
        this.expandedElement = null;
        this.isLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.dataSource = [];
        this.expandedElement = null;
        this.isLoading = false;
        this.errorMessage = this.getErrorMessage(error);
      }
    });
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    const responseMessage = typeof error.error === 'string'
      ? error.error
      : error.error?.message || error.error?.body;

    return responseMessage
      ? `Unable to load feedback details (${error.status}): ${responseMessage}`
      : `Unable to load feedback details (${error.status}): ${error.statusText || 'Request failed'}`;
  }

  private toRows(inputData: any): FeedbackDetailsRow[] {
    const accounts = Array.isArray(inputData) ? inputData : inputData?.Accounts;

    if (!Array.isArray(accounts)) {
      return [];
    }

    return accounts.flatMap((account: any) => {
      const contacts = account.Contacts?.length ? account.Contacts : [{}];

      return contacts.map((contact: any) => ({
        AccountId: this.display(account.AccountId),
        AccountName: this.display(account.AccountName),
        Status: this.display(account.Status),
        IsLsoAcknowledged: account.IsLsoAcknowledged === true ? 'Yes' : account.IsLsoAcknowledged === false ? 'No' : '-',
        CustomerAttachment: this.display(contact.CustomerAttachment?.Comments),
        Submitter: this.display(account.Submitter || contact.Submitter),
        QuestionsAndAnswers: Array.isArray(account.QuestionsAndAnswers) ? account.QuestionsAndAnswers : []
      }));
    });
  }

  private display(value: any): string {
    return value === undefined || value === null || value === '' ? '-' : String(value);
  }
}
